import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTimePickerModule } from 'ng-zorro-antd/time-picker';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { ContactService } from '../services/contact.service';
import { TemplateService } from '../services/template.service';
import { ADDRESS_PATTERN } from '../constants/app.constants';
import { ContactUpsertModalData, ContactUpsertModalResult, ContactUpsertType } from './contact-upsert-modal.types';
import { shouldSuppressGenericError } from '../utils/http-error.utils';
import { Router } from '@angular/router';
import { getServiceTypeTranslationKey, getServiceTypes, normalizeServiceTypes, SERVICE_TYPE_OPTIONS } from '../utils/service-type.utils';
import { isValidGermanMobileNumber, isValidGermanPhoneNumber } from '../utils/validators/phone.validator';

const roleMappings: Record<string, { emailHeader: string; phoneHeader: string }> = {
  'Property Manager': {
    emailHeader: 'PROPERTY_MANAGER_EMAIL',
    phoneHeader: 'PROPERTY_MANAGER_PHONE',
  },
  'Technical Assets Manager': {
    emailHeader: 'TECHNICAL_ASSETS_MANAGER_EMAIL',
    phoneHeader: 'TECHNICAL_ASSETS_MANAGER_PHONE',
  },
  'Condominium Manager': {
    emailHeader: 'CONDOMINIUM_MANAGER_EMAIL',
    phoneHeader: 'CONDOMINIUM_MANAGER_PHONE',
  },
  'Accountant': {
    emailHeader: 'ACCOUNTANT_EMAIL',
    phoneHeader: 'ACCOUNTANT_PHONE',
  },
};

@Component({
  selector: 'app-contact-upsert-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    NzFormModule,
    NzInputModule,
    NzSelectModule,
    NzTimePickerModule,
    NzButtonModule,
    NzIconModule,
    NzToolTipModule,
    NzPopoverModule,
    NzModalModule,
  ],
  templateUrl: './contact-upsert-modal.component.html',
  styleUrl: './contact-upsert-modal.component.less'
})
export class ContactUpsertModalComponent implements OnInit {
  contactForm!: FormGroup;
  weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  headers: string[] = [];
  serviceOptions: Array<{ label: string; value: string }> = [];
  roleOptions: { label: string; value: string }[] = [];
  private confirmedServiceTypes: string[] = [];
  private protectedServiceTypes: string[] | null = null;
  private serviceTypeChangeGuard = false;
  private serviceTypeCheckRequestId = 0;
  private mondayBulkCopyOffered = false;

  showEmailFieldModal = false;
  showPhoneFieldModal = false;

  get type(): ContactUpsertType {
    return this.data.type;
  }

  get editingType(): ContactUpsertType {
    return this.type;
  }

  get editingContact(): any | null {
    return this.data.contact || null;
  }

  get lockRole(): boolean {
    return !!this.data.lockRole;
  }

  get forcedRole(): string | null {
    return this.data.forcedRole || null;
  }

  get lockServiceType(): boolean {
    return !!this.data.lockServiceType;
  }

  get forcedServiceType(): string | null {
    return this.data.forcedServiceType || null;
  }

  get isContactListServiceFlow(): boolean {
    return this.type === 'service'
      && !this.lockServiceType
      && this.data.origin === 'contact-list';
  }

  get showServiceTypeDropdownFooter(): boolean {
    return this.isContactListServiceFlow && this.serviceOptions.length > 0;
  }

  get title(): string {
    if (this.editingContact) {
      return this.translate.instant('CONTACTS.EDIT_CONTACT');
    }
    return this.translate.instant(`CONTACTS.ADD_${this.type.toUpperCase()}`);
  }

  get contactPersons(): FormArray<FormGroup> {
    return this.contactForm.get('contactPersons') as FormArray<FormGroup>;
  }

  constructor(
    private fb: FormBuilder,
    private contactService: ContactService,
    private translate: TranslateService,
    private message: NzMessageService,
    private templateService: TemplateService,
    private router: Router,
    private modalService: NzModalService,
    private modalRef: NzModalRef<ContactUpsertModalResult | null>,
    @Inject(NZ_MODAL_DATA) public data: ContactUpsertModalData
  ) {}

  ngOnInit(): void {
    this.loadHeaders();
  }

  private getServiceOptionLabel(serviceType: string): string {
    const translationKey = getServiceTypeTranslationKey(serviceType);
    if (!translationKey) {
      return serviceType;
    }

    return this.translate.instant(`TECHNICAL_SERVICES.${translationKey}`);
  }

  private buildServiceOptions(): Array<{ label: string; value: string }> {
    const options = SERVICE_TYPE_OPTIONS
      .filter((service) => this.headers.includes(service.translationKey))
      .map((service) => ({
        label: this.translate.instant(`TECHNICAL_SERVICES.${service.translationKey}`),
        value: service.value,
      }));

    const assignedTypes = normalizeServiceTypes(
      getServiceTypes(this.editingContact),
      this.forcedServiceType
    );

    const optionMap = new Map(options.map((option) => [option.value, option]));

    assignedTypes.forEach((serviceType) => {
      if (optionMap.has(serviceType)) {
        return;
      }

      optionMap.set(serviceType, {
        value: serviceType,
        label: this.getServiceOptionLabel(serviceType),
      });
    });

    return Array.from(optionMap.values());
  }

  private loadHeaders(): void {
    this.templateService.getSavedHeaders().subscribe({
      next: (savedHeaders) => {
        this.headers = [];
        if (savedHeaders.propertyManagerHeaders) this.headers.push(...savedHeaders.propertyManagerHeaders);
        if (savedHeaders.technicalAssetsManagerHeaders) this.headers.push(...savedHeaders.technicalAssetsManagerHeaders);
        if (savedHeaders.sevManagerHeaders) this.headers.push(...savedHeaders.sevManagerHeaders);
        if (savedHeaders.accountantHeaders) this.headers.push(...savedHeaders.accountantHeaders);
        if (savedHeaders.caretakerHeaders) this.headers.push(...savedHeaders.caretakerHeaders);
        if (savedHeaders.technicalServiceHeaders) this.headers.push(...savedHeaders.technicalServiceHeaders);

        this.translate
          .get(['ROLES.PROPERTY_MANAGER', 'ROLES.TECHNICAL_ASSETS_MANAGER', 'ROLES.CONDOMINIUM_MANAGER', 'ROLES.ACCOUNTANT'])
          .subscribe((translations) => {
            this.roleOptions = [
              { label: translations['ROLES.PROPERTY_MANAGER'], value: 'Property Manager' },
              { label: translations['ROLES.TECHNICAL_ASSETS_MANAGER'], value: 'Technical Assets Manager' },
              { label: translations['ROLES.CONDOMINIUM_MANAGER'], value: 'Condominium Manager' },
              { label: translations['ROLES.ACCOUNTANT'], value: 'Accountant' },
            ].filter((option) => {
              const roleNameHeader = `${option.value.toUpperCase().replace(/ /g, '_')}_NAME`;
              return this.headers.includes(roleNameHeader);
            });

            this.initForm();
            this.serviceOptions = this.buildServiceOptions();
            if (this.editingContact) {
              this.patchForm(this.editingContact);
            }
            this.applyForcedDefaults();
          });
      },
      error: () => {
        this.message.error(this.translate.instant('CONTACTS.HEADERS_FETCH_FAILED'));
        this.initForm();
        this.serviceOptions = this.buildServiceOptions();
        if (this.editingContact) {
          this.patchForm(this.editingContact);
        }
        this.applyForcedDefaults();
      }
    });
  }

  private initForm(): void {
    this.contactForm = this.fb.group({
      name: [null, Validators.required],
      email: [null],
      phone: [null],
      mobile: [null],
    }, {
      validators: this.atLeastOnePhoneOrMobileValidator.bind(this)
    });

    if (this.type === 'service') {
      this.contactForm.addControl('serviceTypes', this.fb.control<string[]>([], Validators.required));
      this.contactForm.addControl('emergencyNumber', this.fb.control(null, [this.emergencyNumberValidator]));
      this.contactForm.addControl('serviceNote', this.fb.control(null, [Validators.maxLength(80)]));
      this.contactForm.get('email')?.setValidators([Validators.email]);
      this.contactForm.get('phone')?.setValidators([this.phoneValidator]);
      this.contactForm.get('mobile')?.setValidators([this.mobileValidator]);
      this.contactForm.get('serviceTypes')?.valueChanges.subscribe((value) => {
        this.handleServiceTypesChange(value);
      });
    }

    if (this.type === 'employee') {
      this.contactForm.addControl('role', this.fb.control(null, Validators.required));
      const role = this.editingContact?.role || this.contactForm.get('role')?.value;
      this.adjustFieldsBasedOnRole(role);
    }

    if (this.type === 'caretaker') {
      this.contactForm.addControl('address', this.fb.control(null, [this.addressValidator.bind(this)]));
      this.contactForm.addControl('contactPersons', this.fb.array<FormGroup>([], [this.contactPersonsDuplicateValidator.bind(this)]));
      this.contactForm.addControl('emergencyNumber', this.fb.control(null, [this.emergencyNumberValidator]));
      this.contactForm.addControl('note', this.fb.control(null, [Validators.maxLength(80)]));
      this.addAvailabilityControls();
      this.contactForm.get('email')?.setValidators([Validators.email]);
      this.contactForm.get('phone')?.setValidators([this.phoneValidator]);
      this.contactForm.get('mobile')?.setValidators([this.mobileValidator]);
    }

    this.contactForm.get('email')?.updateValueAndValidity();
    this.contactForm.get('phone')?.updateValueAndValidity();
    this.contactForm.get('mobile')?.updateValueAndValidity();
    this.contactForm.updateValueAndValidity({ emitEvent: false });
  }

  private applyForcedDefaults(): void {
    if (this.type === 'employee' && this.forcedRole) {
      this.contactForm.patchValue({ role: this.forcedRole });
      this.adjustFieldsBasedOnRole(this.forcedRole);
    }

    if (this.type === 'service' && this.forcedServiceType) {
      this.setServiceTypesValue([this.forcedServiceType]);
      this.confirmedServiceTypes = normalizeServiceTypes([this.forcedServiceType]);
    }
  }

  private patchForm(contact: any): void {
    this.contactForm.patchValue({
      name: contact.name || contact.serviceName,
      email: contact.email,
      phone: contact.phone,
      mobile: contact.mobile,
      emergencyNumber: contact.emergencyNumber || null,
    });

    if (this.type === 'employee') {
      this.contactForm.patchValue({ role: contact.role });
      this.adjustFieldsBasedOnRole(contact.role);
    }

    if (this.type === 'caretaker') {
      this.contactForm.patchValue({
        address: contact.address,
        emergencyNumber: contact.emergencyNumber || null,
        note: contact.note || null,
      });
      this.patchContactPersons(contact);
      this.weekDays.forEach((day) => {
        this.contactForm.patchValue({
          [`${day}StartTime`]: contact.availability?.[day]?.start ? new Date(contact.availability[day].start) : null,
          [`${day}EndTime`]: contact.availability?.[day]?.end ? new Date(contact.availability[day].end) : null,
        });
      });
    }

    if (this.type === 'service') {
      const serviceTypes = getServiceTypes(contact);
      this.setServiceTypesValue(serviceTypes);
      this.confirmedServiceTypes = serviceTypes;
      this.loadProtectedServiceTypes(serviceTypes);
      this.contactForm.patchValue({
        email: contact.email ?? null,
        serviceNote: contact.serviceNote ?? null
      });
    }
  }

  private createContactPersonGroup(person?: any): FormGroup {
    return this.fb.group({
      _id: [person?._id || null],
      name: [person?.name || null, [Validators.required, Validators.maxLength(120)]],
      position: [person?.position || null, [Validators.maxLength(120)]],
      phone: [person?.phone || null, [Validators.maxLength(64), this.phoneValidator]],
      mobile: [person?.mobile || null, [Validators.maxLength(64), this.mobileValidator]],
      email: [person?.email || null, [Validators.maxLength(254), Validators.email]],
      isPrimary: [person?.isPrimary === true],
    });
  }

  private patchContactPersons(contact: any): void {
    const control = this.contactPersons;
    control.clear();
    const provided = Array.isArray(contact?.contactPersons)
      ? contact.contactPersons
      : (typeof contact?.contactPerson === 'string' && contact.contactPerson.trim()
        ? [{ name: contact.contactPerson.trim(), isPrimary: true }]
        : []);

    const seenIds = new Set<string>();
    const signatures = new Set<string>();
    provided.forEach((person: any) => {
      const id = typeof person?._id === 'string' ? person._id.trim() : '';
      const name = typeof person?.name === 'string' ? person.name.trim() : '';
      const signature = [name, person?.position, person?.phone, person?.mobile, person?.email]
        .map((value) => String(value || '').trim().toLocaleLowerCase('de-DE'))
        .join('|');
      if (!name || (id && seenIds.has(id)) || signatures.has(signature)) {
        return;
      }
      if (id) seenIds.add(id);
      signatures.add(signature);
      control.push(this.createContactPersonGroup(person));
    });
    this.ensurePrimaryContactPerson();
  }

  addContactPerson(): void {
    this.contactPersons.push(this.createContactPersonGroup({
      isPrimary: this.contactPersons.length === 0,
    }));
    this.contactPersons.markAsDirty();
  }

  removeContactPerson(index: number): void {
    this.contactPersons.removeAt(index);
    this.ensurePrimaryContactPerson();
    this.contactPersons.markAsDirty();
  }

  setPrimaryContactPerson(index: number): void {
    this.contactPersons.controls.forEach((control, currentIndex) => {
      control.get('isPrimary')?.setValue(currentIndex === index, { emitEvent: false });
    });
    this.contactPersons.updateValueAndValidity();
    this.contactPersons.markAsDirty();
  }

  private ensurePrimaryContactPerson(): void {
    if (!this.contactPersons?.length) {
      return;
    }
    const primaryIndex = this.contactPersons.controls.findIndex((control) => control.get('isPrimary')?.value === true);
    this.setPrimaryContactPerson(primaryIndex >= 0 ? primaryIndex : 0);
    this.contactPersons.markAsPristine();
  }

  private contactPersonsDuplicateValidator(control: AbstractControl): ValidationErrors | null {
    const entries = Array.isArray(control.value) ? control.value : [];
    const signatures = new Set<string>();
    for (const entry of entries) {
      if (!entry || typeof entry !== 'object') continue;
      const name = String(entry.name || '').trim().toLocaleLowerCase('de-DE');
      if (!name) continue;
      const signature = [name, entry.position, entry.phone, entry.mobile, entry.email]
        .map((value) => String(value || '').trim().toLocaleLowerCase('de-DE'))
        .join('|');
      if (signatures.has(signature)) {
        return { duplicateContactPerson: true };
      }
      signatures.add(signature);
    }
    return null;
  }

  contactPersonCountKey(): string {
    return this.contactPersons.length === 1
      ? 'CONTACTS.CONTACT_PERSON_COUNT_ONE'
      : 'CONTACTS.CONTACT_PERSON_COUNT_OTHER';
  }

  private loadProtectedServiceTypes(serviceTypes: string[]): void {
    if (this.type !== 'service' || !this.editingContact?._id) {
      this.protectedServiceTypes = [];
      return;
    }

    const normalizedTypes = getServiceTypes({ serviceTypes });
    const requestId = ++this.serviceTypeCheckRequestId;
    this.contactService.getAssignedServiceTypes(this.editingContact._id, normalizedTypes).subscribe({
      next: (response) => {
        if (requestId !== this.serviceTypeCheckRequestId) {
          return;
        }
        this.protectedServiceTypes = getServiceTypes({ serviceTypes: response?.assignedServiceTypes });
      },
      error: () => {
        if (requestId !== this.serviceTypeCheckRequestId) {
          return;
        }
        this.protectedServiceTypes = null;
      }
    });
  }

  private setServiceTypesValue(serviceTypes: string[]): void {
    const normalizedTypes = getServiceTypes({ serviceTypes });
    this.serviceTypeChangeGuard = true;
    this.contactForm.get('serviceTypes')?.setValue(normalizedTypes, { emitEvent: false });
    this.serviceTypeChangeGuard = false;
  }

  private handleServiceTypesChange(value: string[] | null): void {
    if (this.type !== 'service' || this.serviceTypeChangeGuard) {
      return;
    }

    const nextTypes = getServiceTypes({ serviceTypes: value || [] });
    const previousTypes = getServiceTypes({ serviceTypes: this.confirmedServiceTypes });
    const removedTypes = previousTypes.filter((serviceType) => !nextTypes.includes(serviceType));

    if (!this.editingContact?._id) {
      this.confirmedServiceTypes = nextTypes;
      return;
    }

    if (nextTypes.length === previousTypes.length) {
      this.confirmedServiceTypes = nextTypes;
      return;
    }

    if (nextTypes.length > previousTypes.length) {
      this.confirmedServiceTypes = nextTypes;
      return;
    }

    const blockedTypes = (this.protectedServiceTypes || []).filter((serviceType) => removedTypes.includes(serviceType));

    if (blockedTypes.length) {
      this.setServiceTypesValue(previousTypes);
      this.confirmedServiceTypes = previousTypes;
      this.contactForm.get('serviceTypes')?.markAsPristine();
      this.contactForm.updateValueAndValidity({ emitEvent: false });
      this.message.info(this.translate.instant('CONTACTS.SERVICE_DELETE_ASSIGNED'));
      return;
    }

    this.setServiceTypesValue(previousTypes);
    const requestId = ++this.serviceTypeCheckRequestId;
    this.contactService.getAssignedServiceTypes(this.editingContact._id, removedTypes).subscribe({
      next: (response) => {
        if (requestId !== this.serviceTypeCheckRequestId) {
          return;
        }

        const confirmedBlockedTypes = getServiceTypes({ serviceTypes: response?.assignedServiceTypes });
        this.protectedServiceTypes = getServiceTypes({
          serviceTypes: [...(this.protectedServiceTypes || []), ...confirmedBlockedTypes]
        });

        if (confirmedBlockedTypes.length) {
          this.setServiceTypesValue(previousTypes);
          this.confirmedServiceTypes = previousTypes;
          this.contactForm.get('serviceTypes')?.markAsPristine();
          this.contactForm.updateValueAndValidity({ emitEvent: false });
          this.message.info(this.translate.instant('CONTACTS.SERVICE_DELETE_ASSIGNED'));
          return;
        }

        this.setServiceTypesValue(nextTypes);
        this.confirmedServiceTypes = nextTypes;
      },
      error: (error) => {
        if (requestId !== this.serviceTypeCheckRequestId) {
          return;
        }

        this.setServiceTypesValue(previousTypes);
        this.confirmedServiceTypes = previousTypes;
        this.contactForm.get('serviceTypes')?.markAsPristine();
        this.contactForm.updateValueAndValidity({ emitEvent: false });
        this.showContactSaveError(error, 'UPDATE');
      }
    });
  }

  private normalizeOptionalText(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }

  onRoleChange(newRole: string): void {
    this.adjustFieldsBasedOnRole(newRole);
  }

  adjustFieldsBasedOnRole(role: string): void {
    this.resetRoleFields();

    const selectedRoleMapping = roleMappings[role];
    if (selectedRoleMapping) {
      this.showEmailFieldModal = this.headers.includes(selectedRoleMapping.emailHeader);
      this.showPhoneFieldModal = this.headers.includes(selectedRoleMapping.phoneHeader);
    }

    this.updateFieldValidation();
  }

  private updateFieldValidation(): void {
    if (this.showEmailFieldModal) {
      this.contactForm.get('email')?.setValidators([Validators.required, Validators.email]);
    } else {
      this.contactForm.get('email')?.clearValidators();
    }

    if (this.showPhoneFieldModal) {
      this.contactForm.get('phone')?.setValidators([this.phoneValidator]);
      this.contactForm.get('mobile')?.setValidators([this.mobileValidator]);
    } else {
      this.contactForm.get('phone')?.clearValidators();
      this.contactForm.get('mobile')?.clearValidators();
    }

    this.contactForm.get('email')?.updateValueAndValidity();
    this.contactForm.get('phone')?.updateValueAndValidity();
    this.contactForm.get('mobile')?.updateValueAndValidity();
    this.contactForm.updateValueAndValidity({ emitEvent: false });
  }

  private resetRoleFields(): void {
    this.showEmailFieldModal = false;
    this.showPhoneFieldModal = false;

    this.contactForm.get('email')?.clearValidators();
    this.contactForm.get('phone')?.clearValidators();
    this.contactForm.get('mobile')?.clearValidators();
    this.contactForm.get('email')?.updateValueAndValidity();
    this.contactForm.get('phone')?.updateValueAndValidity();
    this.contactForm.get('mobile')?.updateValueAndValidity();
  }

  addAvailabilityControls(): void {
    this.weekDays.forEach((day) => {
      this.contactForm.addControl(`${day}StartTime`, this.fb.control(null));
      this.contactForm.addControl(`${day}EndTime`, this.fb.control(null));
    });
  }

  addressValidator(control: AbstractControl): { [key: string]: boolean } | null {
    if (control.value && !ADDRESS_PATTERN.test(control.value)) {
      return { invalidAddress: true };
    }
    return null;
  }

  phoneValidator(control: any): { [key: string]: boolean } | null {
    if (control.value && !isValidGermanPhoneNumber(control.value)) {
      return { invalidPhone: true };
    }
    return null;
  }

  mobileValidator(control: any): { [key: string]: boolean } | null {
    if (control.value && !isValidGermanMobileNumber(control.value)) {
      return { invalidPhone: true };
    }
    return null;
  }

  emergencyNumberValidator(control: any): { [key: string]: boolean } | null {
    if (control.value && !isValidGermanPhoneNumber(control.value)) {
      return { invalidPhone: true };
    }
    return null;
  }

  shouldRequirePhoneOrMobile(): boolean {
    if (this.type === 'service' || this.type === 'caretaker') {
      return true;
    }

    if (this.type === 'employee') {
      return this.showPhoneFieldModal;
    }

    return false;
  }

  atLeastOnePhoneOrMobileValidator(control: AbstractControl): ValidationErrors | null {
    if (!this.shouldRequirePhoneOrMobile()) {
      return null;
    }

    const phone = String(control.get('phone')?.value || '').trim();
    const mobile = String(control.get('mobile')?.value || '').trim();

    if (phone || mobile) {
      return null;
    }

    return { phoneOrMobileRequired: true };
  }

  onStartTimeChange(day: string): void {
    const startTimeControl = this.contactForm.get(`${day}StartTime`);
    const endTimeControl = this.contactForm.get(`${day}EndTime`);
    if (startTimeControl && endTimeControl && endTimeControl.value < startTimeControl.value) {
      endTimeControl.setValue(null);
    }
    this.checkMondayBulkCopy();
  }

  onEndTimeChange(day: string): void {
    this.checkMondayBulkCopy();
  }

  private checkMondayBulkCopy(): void {
    if (this.mondayBulkCopyOffered) return;

    const start = this.contactForm.get('MondayStartTime')?.value;
    const end = this.contactForm.get('MondayEndTime')?.value;

    if (!start || !end) return;

    this.mondayBulkCopyOffered = true;

    const modalRef = this.modalService.confirm({
      nzTitle: this.translate.instant('COMPANY_PAGE.BULK_COPY_TITLE'),
      nzContent: this.translate.instant('COMPANY_PAGE.BULK_COPY_DESCRIPTION'),
      nzOnOk: () => {
        this.applyBulkMondaySlots(start, end);
      }
    });
  }

  private applyBulkMondaySlots(start: Date, end: Date): void {
    const remainingDays = ['Tuesday', 'Wednesday', 'Thursday', 'Friday'];
    remainingDays.forEach((day) => {
      this.contactForm.get(`${day}StartTime`)?.setValue(new Date(start));
      this.contactForm.get(`${day}EndTime`)?.setValue(new Date(end));
    });
    this.contactForm.markAsDirty();
  }

  getDisabledEndHours(day: string): () => number[] {
    return () => {
      const startTime = this.contactForm.get(`${day}StartTime`)?.value;
      if (!startTime) return [];
      const startHour = startTime.getHours();
      return Array.from({ length: startHour }, (_, i) => i);
    };
  }

  getDisabledEndMinutes(day: string): (hour: number) => number[] {
    return (hour: number) => {
      const startTime = this.contactForm.get(`${day}StartTime`)?.value;
      if (!startTime) return [];
      const startHour = startTime.getHours();
      const startMinute = startTime.getMinutes();
      if (hour === startHour) {
        return Array.from({ length: startMinute }, (_, i) => i);
      }
      return [];
    };
  }

  getDisabledEndSeconds(day: string): (hour: number, minute: number) => number[] {
    return (hour: number, minute: number) => {
      const startTime = this.contactForm.get(`${day}StartTime`)?.value;
      if (!startTime) return [];
      const startHour = startTime.getHours();
      const startMinute = startTime.getMinutes();
      const startSecond = startTime.getSeconds();
      if (hour === startHour && minute === startMinute) {
        return Array.from({ length: startSecond }, (_, i) => i);
      }
      return [];
    };
  }

  private showContactSaveError(err: any, action: 'ADD' | 'UPDATE'): void {
    if (shouldSuppressGenericError(err)) {
      return;
    }

    const body = err?.error ?? err;
    if (body?.message === 'SERVICE_ALREADY_EXISTS') {
      const existingService = body?.existingService || {};
      const serviceLabel = [existingService.serviceName || this.contactForm.get('name')?.value || '', existingService.phone || existingService.mobile || '']
        .filter((value: string) => !!String(value || '').trim())
        .join(' - ');
      this.message.error(this.translate.instant('ERROR_MESSAGES.SERVICE_ALREADY_EXISTS_GUIDANCE', {
        serviceLabel
      }));
      return;
    }

    if (body?.message === 'SERVICE_DELETE_ASSIGNED') {
      if (this.type === 'service' && this.editingContact) {
        const currentTypes = getServiceTypes({ serviceTypes: this.contactForm.get('serviceTypes')?.value });
        const blockedTypes = getServiceTypes({ serviceTypes: body?.blockedServiceTypes });
        const restoredTypes = blockedTypes.length
          ? getServiceTypes({ serviceTypes: [...currentTypes, ...blockedTypes] })
          : getServiceTypes(this.editingContact);
        this.setServiceTypesValue(restoredTypes);
        this.confirmedServiceTypes = restoredTypes;
        this.contactForm.get('serviceTypes')?.markAsPristine();
        this.contactForm.updateValueAndValidity({ emitEvent: false });
      }
      this.message.info(this.translate.instant('CONTACTS.SERVICE_DELETE_ASSIGNED'));
      return;
    }

    const beCode = body?.code || body?.errors?.[0]?.code || body?.message;

    if (beCode) {
      const i18nKey = `ERROR_MESSAGES.${beCode}`;
      const translated = this.translate.instant(i18nKey);
      if (translated && translated !== i18nKey) {
        this.message.error(translated);
        return;
      }
    }

    this.message.error(
      this.translate.instant(action === 'ADD' ? 'ERROR_MESSAGES.ADD_FAILED' : 'ERROR_MESSAGES.UPDATE_FAILED')
    );
  }

  save(): void {
    if (!this.contactForm) {
      return;
    }

    this.contactForm.updateValueAndValidity({ emitEvent: false });

    if (!this.contactForm.valid) {
      this.markFormGroupForValidation(this.contactForm);
      this.displayFormErrors();
      return;
    }

    const formValues = this.contactForm.value;
    const contactData: any = {
      name: formValues.name,
      email: formValues.email,
      phone: formValues.phone,
    };

    if (this.type === 'employee' && this.showPhoneFieldModal) {
      contactData.mobile = formValues.mobile || null;
    }

    if (this.type === 'employee') {
      contactData.role = formValues.role;
    } else if (this.type === 'caretaker') {
      contactData.role = 'Caretaker';
      contactData.address = formValues.address;
      contactData.contactPersons = (Array.isArray(formValues.contactPersons) ? formValues.contactPersons : [])
        .map((person: any) => ({
          ...(typeof person?._id === 'string' && person._id.trim() ? { _id: person._id.trim() } : {}),
          name: this.normalizeOptionalText(person?.name) || '',
          position: this.normalizeOptionalText(person?.position),
          phone: this.normalizeOptionalText(person?.phone),
          mobile: this.normalizeOptionalText(person?.mobile),
          email: this.normalizeOptionalText(person?.email),
          isPrimary: person?.isPrimary === true,
        }));
      contactData.emergencyNumber = formValues.emergencyNumber || null;
      contactData.note = this.normalizeOptionalText(formValues.note);
    }

    if (this.type === 'caretaker') {
      contactData.availability = {};
      this.weekDays.forEach((day) => {
        contactData.availability[day] = {
          start: formValues[`${day}StartTime`]?.toISOString(),
          end: formValues[`${day}EndTime`]?.toISOString(),
        };
      });
    }

    if (this.type === 'caretaker' || this.type === 'service') {
      contactData.mobile = formValues.mobile || null;
    }

    if (this.type === 'service') {
      contactData.serviceTypes = getServiceTypes({ serviceTypes: formValues.serviceTypes });
      contactData.emergencyNumber = formValues.emergencyNumber || null;
      contactData.email = this.normalizeOptionalText(formValues.email);
      contactData.serviceNote = this.normalizeOptionalText(formValues.serviceNote);
    }

    if (this.editingContact?._id) {
      if (this.type === 'employee' || this.type === 'caretaker') {
        this.contactService.updateEmployee(this.editingContact._id, contactData).subscribe({
          next: (entity) => {
            this.message.success(this.translate.instant('SUCCESS_MESSAGES.CONTACT_UPDATED'));
            this.modalRef.close({ type: this.type, entity: entity || { ...contactData, _id: this.editingContact._id } });
          },
          error: (error) => {
            this.showContactSaveError(error, 'UPDATE');
          }
        });
      } else {
        this.contactService.updateService(this.editingContact._id, contactData).subscribe({
          next: (entity) => {
            this.message.success(this.translate.instant('SUCCESS_MESSAGES.CONTACT_UPDATED'));
            this.modalRef.close({ type: this.type, entity: entity || { ...contactData, _id: this.editingContact._id } });
          },
          error: (error) => {
            this.showContactSaveError(error, 'UPDATE');
          }
        });
      }
      return;
    }

    if (this.type === 'employee' || this.type === 'caretaker') {
      this.contactService.addEmployee(contactData).subscribe({
        next: (entity) => {
          this.message.success(this.translate.instant('SUCCESS_MESSAGES.CONTACT_ADDED'));
          this.modalRef.close({ type: this.type, entity });
        },
        error: (error) => {
          this.showContactSaveError(error, 'ADD');
        }
      });
    } else {
      this.contactService.addService(contactData).subscribe({
        next: (entity) => {
          this.message.success(this.translate.instant('SUCCESS_MESSAGES.CONTACT_ADDED'));
          this.modalRef.close({ type: this.type, entity });
        },
        error: (error) => {
          this.showContactSaveError(error, 'ADD');
        }
      });
    }
  }

   private markFormGroupForValidation(control: AbstractControl): void {
    control.markAsTouched();
    control.markAsDirty();
    control.updateValueAndValidity();

    if (control instanceof FormGroup || control instanceof FormArray) {
      Object.values(control.controls).forEach(child => {
        this.markFormGroupForValidation(child);
      });
    }
  }

  displayFormErrors(): void {
    if (this.contactForm.get('name')?.invalid) {
      this.message.error(this.translate.instant('FORM_ERRORS.NAME_REQUIRED'));
    }

    if (
      this.type === 'caretaker'
      && this.contactPersons.controls.some((person) => person.get('name')?.hasError('required'))
    ) {
      this.message.error(this.translate.instant('CONTACTS.CONTACT_PERSON_NAME_REQUIRED'));
    }

    if (this.type === 'employee') {
      if (this.contactForm.get('email')?.invalid) {
        if (this.contactForm.get('email')?.hasError('required')) {
          this.message.error(this.translate.instant('FORM_ERRORS.EMAIL_REQUIRED'));
        } else if (this.contactForm.get('email')?.hasError('email')) {
          this.message.error(this.translate.instant('FORM_ERRORS.EMAIL_INVALID'));
        }
      }
    } else if (this.showEmailFieldModal && this.contactForm.get('email')?.invalid) {
      if (this.contactForm.get('email')?.hasError('required')) {
        this.message.error(this.translate.instant('FORM_ERRORS.EMAIL_REQUIRED'));
      } else if (this.contactForm.get('email')?.hasError('email')) {
        this.message.error(this.translate.instant('FORM_ERRORS.EMAIL_INVALID'));
      }
    } else if (this.type === 'caretaker') {
      if (this.contactForm.get('email')?.hasError('email')) {
        this.message.error(this.translate.instant('FORM_ERRORS.EMAIL_INVALID'));
      }
    } else if (this.type === 'service' && this.contactForm.get('email')?.hasError('email')) {
      this.message.error(this.translate.instant('FORM_ERRORS.EMAIL_INVALID'));
    }

    if (this.contactForm.get('phone')?.hasError('invalidPhone')) {
      this.message.error(this.translate.instant('FORM_ERRORS.INVALID_PHONE'));
    }

    if (this.type === 'service' && this.contactForm.get('serviceTypes')?.invalid) {
      this.message.error(this.translate.instant('FORM_ERRORS.SERVICE_TYPE_REQUIRED'));
    }

    if (this.contactForm.hasError('phoneOrMobileRequired')) {
      this.message.error(this.translate.instant('FORM_ERRORS.PHONE_OR_MOBILE_REQUIRED'));
    }

    if (this.type === 'service' && this.contactForm.get('emergencyNumber')?.hasError('invalidPhone')) {
      this.message.error(this.translate.instant('FORM_ERRORS.INVALID_PHONE'));
    }

    if (this.type === 'service' && this.contactForm.get('serviceNote')?.hasError('maxlength')) {
      this.message.error(this.translate.instant('FORM_ERRORS.SERVICE_NOTE_TOO_LONG'));
    }

    if (this.type === 'caretaker' && this.contactForm.get('address')?.hasError('invalidAddress')) {
      this.message.error(this.translate.instant('FORM_ERRORS.ADDRESS_ERROR'));
    }

    if (this.type === 'caretaker' && this.contactForm.get('emergencyNumber')?.hasError('invalidPhone')) {
      this.message.error(this.translate.instant('FORM_ERRORS.INVALID_PHONE'));
    }

    if (this.type === 'caretaker' && this.contactForm.get('note')?.hasError('maxlength')) {
      this.message.error(this.translate.instant('FORM_ERRORS.CARETAKER_NOTE_TOO_LONG'));
    }

    if (this.type === 'caretaker' && this.contactPersons?.hasError('duplicateContactPerson')) {
      this.message.error(this.translate.instant('CONTACTS.CONTACT_PERSON_DUPLICATE'));
    }

    if (this.contactForm.get('mobile')?.hasError('invalidPhone')) {
      this.message.error(this.translate.instant('FORM_ERRORS.INVALID_PHONE'));
    }

    if (this.type === 'employee' && this.contactForm.get('role')?.invalid) {
      this.message.error(this.translate.instant('CONTACTS.ROLE_REQUIRED'));
    }
  }

  cancel(): void {
    this.modalRef.close(null);
  }

  goToConfigureLandingPage(): void {
    this.modalService.confirm({
      nzTitle: this.translate.instant('CONTACTS.UNSAVED_CHANGES_TITLE'),
      nzContent: this.translate.instant('CONTACTS.UNSAVED_CHANGES_DESC'),
      nzOkText: this.translate.instant('CONTACTS.SERVICE_TYPE_FOOTER_CTA'),
      nzCancelText: this.translate.instant('COMMON.CANCEL'),
      nzOnOk: () => {
        this.modalRef.close(null);
        this.router.navigate(['/properties'], {
          queryParams: { openExcelWizard: '1' },
          queryParamsHandling: 'merge'
        });
      }
    });
  }
}
